/**
 * Paso 3 de la carga: el consentimiento y las etiquetas de toda la carga (SPEC-BD §6.2).
 *
 * - Si el archivo trae una columna de consentimiento, se muestra qué dice fila por fila (sin mirar la
 *   declaración): así el operador ve antes de declarar que, por ejemplo, 40 filas dicen «no».
 * - «Todo este archivo tiene consentimiento» arranca APAGADO (fail-closed, D3) y, encendido, pide cómo,
 *   cuándo y la constancia. Una declaración a medias no deja seguir (`validarDeclaracionLocal`).
 */

import { useId } from "react";
import { Loader2 } from "lucide-react";
import type { ConteosCarga } from "../../types.base";
import { num } from "../../../campana-nueva/utils/format";
import { FUENTES_CONSENTIMIENTO, FUENTE_OTRA } from "./comun";
import { InputEtiquetas } from "./InputEtiquetas";

export interface EstadoDeclaracion {
  activa: boolean;
  opcion: string;        // una de FUENTES_CONSENTIMIENTO, «Otra» o "" (sin elegir)
  otra: string;          // el texto si eligió «Otra» (≤ 100)
  fecha: string;         // YYYY-MM-DD
  constancia: boolean;
}
export const DECLARACION_VACIA: EstadoDeclaracion = { activa: false, opcion: "", otra: "", fecha: "", constancia: false };

interface Props {
  /** El encabezado de la columna vinculada como consentimiento, o null si no hay. */
  columna: string | null;
  conteoColumna: ConteosCarga["columnaConsentimiento"];
  contandoColumna: boolean;
  errorColumna: string;
  onReintentarColumna: () => void;
  declaracion: EstadoDeclaracion;
  onDeclaracion: (d: EstadoDeclaracion) => void;
  etiquetas: string[];
  onEtiquetas: (e: string[]) => void;
  sugerencias: { clave: string; texto: string }[];
  hoy: string;
  disabled?: boolean;
}

export function FormConsentimiento(p: Props) {
  const d = p.declaracion;
  const set = (c: Partial<EstadoDeclaracion>) => p.onDeclaracion({ ...d, ...c });
  const idInterruptor = useId();
  const idFuente = useId();
  const idOtra = useId();
  const idFecha = useId();
  const idConstancia = useId();

  return (
    <div className="space-y-6">
      {p.columna && (
        <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3 text-sm text-slate-700" aria-live="polite">
          {p.contandoColumna && !p.conteoColumna ? (
            <p className="flex items-center gap-2 text-slate-500"><Loader2 size={14} className="animate-spin" /> {`Leyendo la columna "${p.columna}"…`}</p>
          ) : p.errorColumna ? (
            <p className="text-red-700">
              {`No pudimos leer la columna "${p.columna}". ${p.errorColumna} `}
              <button type="button" onClick={p.onReintentarColumna} className="font-semibold underline">Reintentar</button>
            </p>
          ) : p.conteoColumna ? (
            <p>
              {`En la columna "${p.columna}": `}<strong>{num(p.conteoColumna.si)}</strong> dicen que sí · <strong>{num(p.conteoColumna.no)}</strong> dicen que no ·{" "}
              <strong>{num(p.conteoColumna.vacias)}</strong> vacías · <strong>{num(p.conteoColumna.noReconocidas)}</strong> no se entienden (quedan sin consentimiento)
            </p>
          ) : null}
        </div>
      )}

      <div className="space-y-3">
        <label htmlFor={idInterruptor} className="flex items-center gap-3 cursor-pointer w-fit">
          <input id={idInterruptor} type="checkbox" role="switch" aria-checked={d.activa} checked={d.activa} disabled={p.disabled}
            onChange={(e) => set({ activa: e.target.checked })} className="peer sr-only" />
          <span className={`relative w-10 h-6 rounded-full transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-violet-500 ${d.activa ? "bg-violet-600" : "bg-slate-300"}`}>
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${d.activa ? "translate-x-4" : ""}`} />
          </span>
          <span className="text-sm font-semibold text-slate-900">Todo este archivo tiene consentimiento</span>
        </label>

        {d.activa && (
          <div className="rounded-xl border border-slate-100 p-4 space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor={idFuente} className="text-xs font-semibold text-slate-600">Cómo lo dieron</label>
                <select id={idFuente} value={d.opcion} disabled={p.disabled} onChange={(e) => set({ opcion: e.target.value })}
                  className="w-full mt-1.5 h-10 rounded-xl border border-slate-200 px-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-violet-500">
                  <option value="">Elegí una opción</option>
                  {FUENTES_CONSENTIMIENTO.map((f) => <option key={f} value={f}>{f}</option>)}
                  <option value={FUENTE_OTRA}>{FUENTE_OTRA}</option>
                </select>
              </div>
              <div>
                <label htmlFor={idFecha} className="text-xs font-semibold text-slate-600">Cuándo</label>
                <input id={idFecha} type="date" value={d.fecha} max={p.hoy} min="2000-01-01" disabled={p.disabled}
                  onChange={(e) => set({ fecha: e.target.value })}
                  className="w-full mt-1.5 h-10 rounded-xl border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
              </div>
            </div>
            {d.opcion === FUENTE_OTRA && (
              <div>
                <label htmlFor={idOtra} className="text-xs font-semibold text-slate-600">Contá cómo lo dieron</label>
                <input id={idOtra} value={d.otra} maxLength={100} disabled={p.disabled} onChange={(e) => set({ otra: e.target.value.slice(0, 100) })}
                  className="w-full mt-1.5 h-10 rounded-xl border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
              </div>
            )}
            <label htmlFor={idConstancia} className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
              <input id={idConstancia} type="checkbox" checked={d.constancia} disabled={p.disabled} onChange={(e) => set({ constancia: e.target.checked })}
                className="mt-0.5 rounded border-slate-300 text-violet-600 focus:ring-violet-500" />
              Tengo constancia de este consentimiento y la puedo mostrar si alguien reclama
            </label>
          </div>
        )}

        <p className="text-xs text-slate-500 leading-relaxed">
          {"La columna de cada fila manda: si una fila dice \"no\", esa persona queda sin consentimiento aunque declares el archivo entero. Sin columna ni declaración, los contactos quedan en la base pero no reciben campañas por WhatsApp oficial."}
        </p>
      </div>

      <InputEtiquetas
        label="Etiquetas para toda la carga"
        ayuda="Por ejemplo: Feria 2026. Sirven para armar públicos."
        valores={p.etiquetas}
        onChange={p.onEtiquetas}
        sugerencias={p.sugerencias}
        disabled={p.disabled}
      />
    </div>
  );
}

export default FormConsentimiento;
